import React, { useState, useEffect } from "react";
import { FaSms, FaShieldAlt, FaClock, FaLock, FaArrowRight } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getOTp, verifyOtp } from "../redux/actions/userActions";
import Loading from "./Loading";

function OTP() {
  const [otp, setOtp] = useState("");
  const [minutes, setMinutes] = useState(4);
  const [seconds, setSeconds] = useState(30);
  const [message, setMessage] = useState("");
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get patient data from location state
  const patient = location.state;
  console.log('location', location);
  // Redux state selectors
  const store = useSelector((state) => state);
  const otpStore = store.verifyOtp;
  const { success, error, data } = store.getOtp;
  const { loading } = store.verifyOtp;

  // Computed values
  const totalSeconds = minutes * 60 + seconds;
  const canResend = totalSeconds === 0;
  const isOtpValid = otp.length === 4;

  // Format time display
  const formatTime = (mins, secs) => {
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle OTP submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isOtpValid || !patient) return;
    
    dispatch(verifyOtp(patient.system_id, patient.mobile, otp));
  };

  // Handle OTP resend
  const handleResend = () => {
    if (!patient) return navigate('/register');
    
    setMinutes(4);
    setSeconds(30);
    setOtp("");
    dispatch(getOTp(patient.system_id, patient.mobile));
    localStorage.setItem('no_otp', JSON.stringify('true'));
  };

  // Timer effect
  useEffect(() => {
    if (totalSeconds > 0) {
      const interval = setInterval(() => {
        if (seconds > 0) {
          setSeconds(seconds - 1);
        } else if (minutes > 0) {
          setSeconds(59);
          setMinutes(minutes - 1);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [minutes, seconds, totalSeconds]);

  // Handle initial OTP request and success navigation
  useEffect(() => {
    // Auto-request OTP if needed
    if (localStorage.getItem('no_otp') === 'false') {
      handleResend();
    }

    // Navigate on successful verification
    if (otpStore.success && patient) {
      localStorage.setItem('phone', JSON.stringify(patient.mobile));
      localStorage.setItem('file_number', JSON.stringify(patient.system_id));
      navigate('/createpassword');
    }
  }, [otpStore.success, patient, navigate]);

  // Redirect if no patient data
  useEffect(() => {
    if (!patient) {
      navigate('/register');
    }
  }, [patient, navigate]);

  // Handle error messages
  useEffect(() => {
    if (error) {
      setMessage(error);
    } else if (otpStore.error) {
      setMessage(otpStore.error);
    } else {
      setMessage("");
    }
  }, [error, otpStore.error]);

  // Show loading overlay when processing
  if (loading) {
    return (
      <Loading 
        variant="medical" 
        size="large" 
        message="Verifying your code..." 
        fullScreen={true} 
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary100 via-blue-500 to-purple-600 flex flex-col justify-center items-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-primary100 to-blue-600 px-8 py-12 text-center">
          <div className="relative">
            <div className="w-20 h-20 bg-white rounded-full flex justify-center items-center mx-auto mb-4 shadow-lg">
              <FaSms className="text-primary100 text-2xl" />
            </div>
            {/* Animated rings */}
            <div className="absolute inset-0 flex justify-center items-center">
              <div className="w-28 h-28 border-2 border-white/30 rounded-full animate-ping"></div>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            Verify Your Phone
          </h1>
          <p className="text-blue-100 text-sm">
            Enter the 4-digit code sent to {patient?.mobile +' | '+patient.email || 'your phone or email'}
          </p>
        </div>

        {/* Security Features */}
        <div className="px-8 py-4 bg-blue-50">
          <div className="flex justify-center space-x-6">
            <div className="flex items-center space-x-2">
              <FaShieldAlt className="text-green-500 text-sm" />
              <span className="text-xs text-gray-600">Secure</span>
            </div>
            <div className="flex items-center space-x-2">
              <FaLock className="text-green-500 text-sm" />
              <span className="text-xs text-gray-600">Encrypted</span>
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className="px-8 py-8">
          {/* Error Message */}
          {message && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm text-center">{message}</p>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-600 text-sm text-center">
                OTP sent successfully!
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* OTP Input */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Verification Code
              </label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="0000"
                className="w-full px-4 py-4 text-center text-2xl font-bold tracking-widest border-2 border-gray-200 rounded-lg focus:border-primary100 focus:ring-2 focus:ring-primary100/20 transition-all duration-200"
                maxLength="4"
                autoComplete="one-time-code"
              />
              <p className="text-xs text-gray-500 mt-2 text-center">
                Enter the 4-digit code sent to your phone
              </p>
            </div>

            {/* Timer Display */}
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <FaClock className="text-primary100" />
                <span className="font-semibold text-gray-700">Time Remaining</span>
              </div>
              <div className="text-2xl font-bold text-primary100">
                {formatTime(minutes, seconds)}
              </div>
              {canResend && (
                <button
                  type="button"
                  onClick={handleResend}
                  className="mt-3 text-primary100 hover:text-blue-600 font-semibold text-sm underline transition-colors duration-200"
                >
                  Resend Code
                </button>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={!isOtpValid || loading}
              className="w-full bg-gradient-to-r from-primary100 to-blue-600 text-white py-4 rounded-lg font-semibold text-lg hover:from-blue-600 hover:to-primary100 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transform hover:scale-[1.02] transition-all duration-200 shadow-lg hover:shadow-xl disabled:transform-none flex items-center justify-center space-x-2"
            >
              {loading ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <span>Verify Code</span>
                  <FaArrowRight />
                </>
              )}
            </button>
          </form>

          {/* Help Text */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Didn't receive the code? Check your spam folder or{" "}
              <span className="text-primary100 hover:underline cursor-pointer">
                contact support
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OTP;
